// Weekly volume + frequency per muscle group (spec section 8 nice-to-have).
// Working-set count per muscle and how many distinct days hit it, against the
// frequency targets in spec section 6. Pure: takes sessions + an exercise lookup.

import type { Exercise, MuscleGroup, Session } from './types';
import { workingSets } from './estimate';

// Frequency targets per muscle/week (spec section 6). Muscles not listed have no
// explicit target (treated as best-effort accessory work).
export const FREQUENCY_TARGETS: Partial<Record<MuscleGroup, number>> = {
  chest: 2,
  back: 2,
  side_delt: 2,
  biceps: 2,
  triceps: 2,
  quad: 2,
  hamstring: 2,
};

// General hypertrophy guidance: ~10-20 hard working sets per muscle per week.
export const VOLUME_RANGE = { low: 10, high: 20 };

export interface MuscleVolume {
  muscle: MuscleGroup;
  workingSets: number;
  sessions: number;
  frequencyTarget: number | null;
  meetsFrequency: boolean;
  inVolumeRange: boolean;
}

export interface VolumeReport {
  weekStart: string;
  weekEnd: string;
  byMuscle: MuscleVolume[];
  totalWorkingSets: number;
}

function inRange(dateISO: string, startISO: string, endISO: string): boolean {
  return dateISO >= startISO && dateISO <= endISO;
}

// Monday-Sunday ISO week containing refDate (YYYY-MM-DD). Local-time agnostic:
// operates on the date string only.
export function isoWeekRange(refDate: string): { start: string; end: string } {
  const d = new Date(refDate + 'T00:00:00');
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: toISODate(start), end: toISODate(end) };
}

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function weeklyVolume(
  sessions: Session[],
  exerciseById: Map<string, Exercise>,
  weekStart: string,
  weekEnd: string,
): VolumeReport {
  const setCount = new Map<MuscleGroup, number>();
  const dayHits = new Map<MuscleGroup, Set<string>>();

  for (const session of sessions) {
    if (!inRange(session.date, weekStart, weekEnd)) continue;
    for (const entry of session.entries) {
      const ex = exerciseById.get(entry.exerciseId);
      if (!ex) continue;
      const sets = workingSets(entry.sets).length;
      if (sets === 0) continue;
      setCount.set(ex.muscleGroup, (setCount.get(ex.muscleGroup) ?? 0) + sets);
      if (!dayHits.has(ex.muscleGroup)) dayHits.set(ex.muscleGroup, new Set());
      dayHits.get(ex.muscleGroup)!.add(session.date);
    }
  }

  const muscles = new Set<MuscleGroup>([...setCount.keys(), ...Object.keys(FREQUENCY_TARGETS) as MuscleGroup[]]);
  const byMuscle: MuscleVolume[] = [...muscles].map((muscle) => {
    const wsets = setCount.get(muscle) ?? 0;
    const days = dayHits.get(muscle)?.size ?? 0;
    const target = FREQUENCY_TARGETS[muscle] ?? null;
    return {
      muscle,
      workingSets: wsets,
      sessions: days,
      frequencyTarget: target,
      meetsFrequency: target == null ? true : days >= target,
      inVolumeRange: wsets >= VOLUME_RANGE.low && wsets <= VOLUME_RANGE.high,
    };
  });

  byMuscle.sort((a, b) => b.workingSets - a.workingSets);
  const totalWorkingSets = byMuscle.reduce((sum, m) => sum + m.workingSets, 0);
  return { weekStart, weekEnd, byMuscle, totalWorkingSets };
}
